import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, List, Grid, Clock, Filter } from 'lucide-react';
import { getBatchClasses, subscribeToBatchClasses } from '../../firebase/collections';
import BatchClassModal from './BatchClassModal';

const BatchSchedule = ({ batchId, startDate, endDate }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayClasses, setSelectedDayClasses] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'week', 'month', 'all', 'list'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'past', 'upcoming'

  useEffect(() => {
    if (!batchId) return;

    setLoading(true);
    
    // Load classes initially
    const loadClasses = async () => {
      const result = await getBatchClasses(batchId);
      if (result.success) {
        setClasses(result.data);
      }
      setLoading(false);
    };

    loadClasses();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToBatchClasses(batchId, (result) => {
      if (result.success) {
        setClasses(result.data);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [batchId]);

  // Initialize current week start to today or batch start date
  useEffect(() => {
    if (!startDate) return;
    
    const batchStart = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // If today is before batch start, use batch start
    // Otherwise, find the Monday of the current week
    let weekStart;
    if (today < batchStart) {
      weekStart = new Date(batchStart);
    } else {
      weekStart = new Date(today);
      const dayOfWeek = weekStart.getDay();
      const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust to Monday
      weekStart.setDate(diff);
    }
    
    weekStart.setHours(0, 0, 0, 0);
    setCurrentWeekStart(weekStart);
  }, [startDate]);

  // Get all classes with proper date handling
  const allClassesWithDates = useMemo(() => {
    return classes.map(c => {
      let classDate;
      if (c.date?.toDate) {
        classDate = c.date.toDate();
      } else if (c.date instanceof Date) {
        classDate = c.date;
      } else if (typeof c.date === 'string') {
        classDate = new Date(c.date);
      } else {
        return null;
      }
      
      // Calculate day number
      const batchStart = new Date(startDate);
      const diffTime = classDate - batchStart;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      return {
        ...c,
        dateObj: classDate,
        dateStr: classDate.toISOString().split('T')[0],
        dayNumber: diffDays > 0 ? diffDays : 1
      };
    }).filter(c => c !== null);
  }, [classes, startDate]);

  // Filter classes by status
  const filteredClasses = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    return allClassesWithDates.filter(c => {
      const classDate = new Date(c.dateObj);
      classDate.setHours(0, 0, 0, 0);
      
      if (filterStatus === 'past') {
        return classDate < now;
      } else if (filterStatus === 'upcoming') {
        return classDate >= now;
      }
      return true; // 'all'
    });
  }, [allClassesWithDates, filterStatus]);

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    if (!startDate || !endDate || !currentWeekStart) return [];

    const days = [];
    let start, end;
    
    if (viewMode === 'week') {
      start = new Date(currentWeekStart);
      end = new Date(start);
      end.setDate(end.getDate() + 6); // Sunday of the week
    } else if (viewMode === 'month') {
      start = new Date(currentWeekStart);
      start.setDate(1); // First day of month
      end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0); // Last day of month
    } else {
      // 'all' view - show entire batch
      start = new Date(startDate);
      end = new Date(endDate);
    }

    // Don't go outside batch dates
    const batchStart = new Date(startDate);
    const batchEnd = new Date(endDate);
    if (start < batchStart) start = new Date(batchStart);
    if (end > batchEnd) end = new Date(batchEnd);

    const current = new Date(start);

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const dayClasses = filteredClasses.filter(c => c.dateStr === dateStr);
      
      // Calculate day number
      const diffTime = current - batchStart;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      days.push({
        date: new Date(current),
        dateStr,
        dayNumber: diffDays > 0 ? diffDays : 1,
        dayName: current.toLocaleDateString('en-US', { weekday: 'long' }),
        classes: dayClasses
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [startDate, endDate, currentWeekStart, viewMode, filteredClasses]);

  // Group days by week
  const weeks = useMemo(() => {
    if (viewMode === 'list') return [];
    if (calendarDays.length === 0) return [];
    
    const weeksArray = [];
    
    // Get the day of week for the first day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const firstDayOfWeek = calendarDays[0]?.date.getDay() || 0;
    
    // Calculate how many nulls to add at the start (to align with Monday)
    // If first day is Sunday (0), we need 6 nulls before it
    // If first day is Monday (1), we need 0 nulls
    // If first day is Tuesday (2), we need 1 null, etc.
    const paddingStart = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Add padding at the start
    const paddedDays = [];
    for (let i = 0; i < paddingStart; i++) {
      paddedDays.push(null);
    }
    paddedDays.push(...calendarDays);
    
    // Calculate padding at the end to complete the last week
    const totalDays = paddedDays.length;
    const remainingDays = totalDays % 7;
    const paddingEnd = remainingDays === 0 ? 0 : 7 - remainingDays;
    
    for (let i = 0; i < paddingEnd; i++) {
      paddedDays.push(null);
    }
    
    // Split into weeks (groups of 7)
    for (let i = 0; i < paddedDays.length; i += 7) {
      weeksArray.push(paddedDays.slice(i, i + 7));
    }

    return weeksArray;
  }, [calendarDays, viewMode]);

  // Navigation functions
  const navigateWeek = (direction) => {
    if (!currentWeekStart) return;
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction * 7));
    
    // Don't go outside batch dates
    const batchStart = new Date(startDate);
    const batchEnd = new Date(endDate);
    
    if (newWeekStart < batchStart) {
      setCurrentWeekStart(new Date(batchStart));
    } else if (newWeekStart > batchEnd) {
      // Find last week that fits
      const lastWeekStart = new Date(batchEnd);
      lastWeekStart.setDate(lastWeekStart.getDate() - 6);
      setCurrentWeekStart(lastWeekStart);
    } else {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const navigateMonth = (direction) => {
    if (!currentWeekStart) return;
    const newMonthStart = new Date(currentWeekStart);
    newMonthStart.setMonth(newMonthStart.getMonth() + direction);
    newMonthStart.setDate(1); // First of month
    
    // Don't go outside batch dates
    const batchStart = new Date(startDate);
    batchStart.setDate(1);
    const batchEnd = new Date(endDate);
    
    if (newMonthStart < batchStart) {
      setCurrentWeekStart(new Date(batchStart));
    } else if (newMonthStart > batchEnd) {
      setCurrentWeekStart(new Date(batchEnd));
    } else {
      setCurrentWeekStart(newMonthStart);
    }
  };

  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const batchStart = new Date(startDate);
    const batchEnd = new Date(endDate);
    
    if (today < batchStart) {
      setCurrentWeekStart(new Date(batchStart));
    } else if (today > batchEnd) {
      setCurrentWeekStart(new Date(batchEnd));
    } else {
      // Find Monday of current week
      const weekStart = new Date(today);
      const dayOfWeek = weekStart.getDay();
      const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      weekStart.setDate(diff);
      setCurrentWeekStart(weekStart);
    }
  };

  const handleDayClick = (day) => {
    if (!day || day.classes.length === 0) return;
    
    setSelectedDayClasses(day.classes);
    setSelectedDay(day);
  };

  const formatTime = (time) => {
    if (!time) return '';
    if (typeof time === 'string') {
      return time;
    }
    // If it's a timestamp or Date object, format it
    const date = time instanceof Date ? time : new Date(time);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (date, short = false) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (short) {
      return d.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
    return d.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // List view - sorted classes (must be before conditional returns)
  const sortedClassesList = useMemo(() => {
    return [...filteredClasses].sort((a, b) => {
      const dateA = new Date(a.dateObj);
      const dateB = new Date(b.dateObj);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      // If same date, sort by time
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  }, [filteredClasses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <Grid className="w-4 h-4 inline mr-1" />
                Week
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  viewMode === 'month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  viewMode === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <List className="w-4 h-4 inline mr-1" />
                List
              </button>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Classes</option>
              <option value="past">Past Classes</option>
              <option value="upcoming">Upcoming Classes</option>
            </select>
          </div>

          {/* Navigation */}
          {viewMode !== 'list' && viewMode !== 'all' && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => viewMode === 'week' ? navigateWeek(-1) : navigateMonth(-1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title={viewMode === 'week' ? 'Previous Week' : 'Previous Month'}
              >
                <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              >
                Today
              </button>
              <button
                onClick={() => viewMode === 'week' ? navigateWeek(1) : navigateMonth(1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title={viewMode === 'week' ? 'Next Week' : 'Next Month'}
              >
                <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
            </div>
          )}
        </div>

        {/* Display current period */}
        {viewMode !== 'list' && currentWeekStart && (
          <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
            {viewMode === 'week' && (
              <span>
                {currentWeekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} -{' '}
                {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {viewMode === 'month' && (
              <span>
                {currentWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
            )}
            {viewMode === 'all' && (
              <span>
                {new Date(startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} -{' '}
                {new Date(endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            All Classes ({sortedClassesList.length})
          </h3>
          <div className="space-y-2">
            {sortedClassesList.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">No classes found</p>
            ) : (
              sortedClassesList.map((classItem) => {
                const classDate = new Date(classItem.dateObj);
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const isPast = classDate < now;
                
                return (
                  <motion.div
                    key={classItem.id}
                    onClick={() => {
                      navigate(`/batch-class/${classItem.id}`);
                    }}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isPast
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                        : 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                    } ${classItem.isRevision ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20' : ''}`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {classItem.subject}
                          </h4>
                          {classItem.isRevision && (
                            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs font-medium">
                              Revision
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            classItem.status === 'completed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                            classItem.status === 'live' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          }`}>
                            {classItem.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>Day {classItem.dayNumber} - {formatDate(classItem.dateObj)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatTime(classItem.time)}</span>
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      {viewMode !== 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Batch Schedule</h2>
            </div>
          </div>

        {/* Week Days Header */}
        <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-x-auto">
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
            const dayAbbr = day.substring(0, 3);
            return (
              <div
                key={day}
                className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0 min-w-0"
              >
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{dayAbbr}</span>
              </div>
            );
          })}
        </div>

          {/* Calendar Grid */}
          <div className="p-2 sm:p-4 overflow-x-auto">
            {weeks.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                No classes found for this period
              </div>
            ) : (
              weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 min-w-[700px] sm:min-w-0">
                  {week.map((day, dayIndex) => {
                    if (!day) {
                      return (
                        <div
                          key={`empty-${dayIndex}`}
                          className="min-h-[100px] sm:min-h-[120px] border border-gray-200 dark:border-gray-700 rounded-lg p-1.5 sm:p-2 bg-gray-50 dark:bg-gray-900"
                        >
                          <div className="text-xs sm:text-sm text-gray-400 dark:text-gray-600 text-center">No Class</div>
                        </div>
                      );
                    }

                    const isRevision = day.classes.some(c => c.isRevision);
                    const hasClasses = day.classes.length > 0;
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const dayDate = new Date(day.date);
                    dayDate.setHours(0, 0, 0, 0);
                    const isPast = dayDate < now;
                    const isToday = dayDate.getTime() === now.getTime();

                    return (
                      <motion.div
                        key={day.dateStr}
                        onClick={() => hasClasses && handleDayClick(day)}
                        className={`min-h-[100px] sm:min-h-[120px] border rounded-lg p-1.5 sm:p-2 transition-all ${
                          hasClasses
                            ? isPast
                              ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                              : 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        } ${isRevision ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-600' : ''} ${isToday ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}`}
                        whileHover={hasClasses ? { scale: 1.02 } : {}}
                        whileTap={hasClasses ? { scale: 0.98 } : {}}
                      >
                        <div className={`text-[10px] sm:text-xs font-semibold mb-1 sm:mb-2 leading-tight ${isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                          <div className="flex flex-col sm:block">
                            <span>Day {day.dayNumber}</span>
                            <span className="hidden sm:inline"> - </span>
                            <span className="sm:hidden block text-[9px] mt-0.5">{formatDate(day.date, true)}</span>
                            <span className="hidden sm:inline">{formatDate(day.date, false)}</span>
                          </div>
                          {isToday && <span className="ml-1 text-blue-600 dark:text-blue-400 text-[9px] sm:text-xs">(Today)</span>}
                        </div>

                        {day.classes.length === 0 ? (
                          <div className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-600 text-center mt-2 sm:mt-4">No Class</div>
                        ) : (
                          <div className="space-y-0.5 sm:space-y-1">
                            {day.classes.slice(0, 2).map((classItem) => (
                              <div
                                key={classItem.id}
                                className="text-[9px] sm:text-xs p-1 sm:p-1.5 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                              >
                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
                                  <span className="text-[8px] sm:text-[10px]">{formatTime(classItem.time)}</span>
                                  <span className="ml-1">{classItem.subject}</span>
                                </div>
                                {classItem.isRevision && (
                                  <div className="text-[8px] sm:text-[10px] text-purple-600 dark:text-purple-400 mt-0.5">
                                    Rev
                                  </div>
                                )}
                              </div>
                            ))}
                            {day.classes.length > 2 && (
                              <div className="text-[9px] sm:text-xs text-blue-600 dark:text-blue-400 font-medium mt-0.5">
                                +{day.classes.length - 2} More
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedDay && (
        <BatchClassModal
          day={selectedDay}
          classes={selectedDayClasses}
          onClose={() => {
            setSelectedDay(null);
            setSelectedDayClasses([]);
          }}
        />
      )}
    </div>
  );
};

export default BatchSchedule;

